// @ts-nocheck
import type { MapRoute } from '@/types/map/map.types'
import {
  bezierSpline,
  center,
  distance,
  lineString,
  length,
  points,
} from '@turf/turf'
import type { LngLatBoundsLike } from 'mapbox-gl'

export const LONG_DISTANCE_ZOOM_LEVEL = 1000

export function normalizeLongitude(longitude: number): number {
  if (longitude > 180) return longitude - 360
  if (longitude < -180) return longitude + 360
  return longitude
}

export function handleAntimeridianCrossing(
  fromLongitude: number,
  toLongitude: number,
): number {
  fromLongitude = normalizeLongitude(fromLongitude)
  toLongitude = normalizeLongitude(toLongitude)

  const delta = Math.abs(fromLongitude - toLongitude)

  if (delta > 180) {
    if (fromLongitude > toLongitude) {
      toLongitude += 360
    } else {
      fromLongitude += 360
    }
  }

  const midpoint = (fromLongitude + toLongitude) / 2

  // This line normalizes the midpoint value to ensure it stays within the range of -180 to 180 degrees.
  // Here's what happens step-by-step:
  // 1. (midpoint + 180): Shift the midpoint to a 0 to 360 range instead of -180 to 180. This avoids negative values during the modulo operation.
  // 2. % 360: This reduces the value to a range between 0 and 360.
  // 3. + 360) % 360: This ensures that if the result from step 2 is negative (which can happen), we add 360 to bring it back within the 0 to 360 range, and then modulo 360 again.
  // 4. - 180: Finally, we subtract 180 to shift the range back to -180 to 180 degrees, the standard longitude range.
  const normalizedMidpoint = ((((midpoint + 180) % 360) + 360) % 360) - 180

  return normalizedMidpoint
}

export function handleAntimeridianCrossingRoutes(
  firstRoute: MapRoute,
  secondRoute: MapRoute,
): [MapRoute, MapRoute] {
  const firstLng = normalizeLongitude(firstRoute.longitude)
  const secondLng = normalizeLongitude(secondRoute.longitude)

  const adjustedSecondLng = handleAntimeridianCrossing(firstLng, secondLng)

  return [
    { longitude: firstRoute.longitude, latitude: firstRoute.latitude },
    { longitude: adjustedSecondLng, latitude: secondRoute.latitude },
  ]
}

export function createBezierSplineFeature(
  coordinates: [number, number][],
): Record<string, any> {
  const line = lineString(coordinates)
  const bezier = bezierSpline(line, {
    stepLength: 10,
    sharpness: 0,
    resolution: 100000,
  } as any)

  return bezier
}

export function convertRoutesToCoordinates(
  routes: Record<string, any>[],
): [number, number][] {
  return routes
    .filter(
      (point) =>
        typeof point.longitude === 'number' &&
        typeof point.latitude === 'number',
    )
    .map((point) => {
      return [point.longitude, point.latitude]
    })
}

export function updateRoutesWithBezierCurve(
  routes: Record<string, any>[],
): [number, number][] {
  const coordinates = convertRoutesToCoordinates(routes)

  if (!coordinates || coordinates.length < 2) {
    return []
  }

  const bezier = createBezierSplineFeature(coordinates)
  return bezier ? bezier.geometry.coordinates : []
}

export function interpolateCoordinate(
  coordStart: [number, number],
  coordEnd: [number, number],
  fraction: number,
): [number, number] {
  return [
    coordStart[0] + (coordEnd[0] - coordStart[0]) * fraction,
    coordStart[1] + (coordEnd[1] - coordStart[1]) * fraction,
  ]
}

export function calculateAnimationDuration(
  coordinates: [number, number][],
  speed: number,
): number {
  const lineString = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: coordinates,
    },
  }

  const distance = length(lineString, { units: 'kilometers' })
  return (distance / speed) * 1000
}

export function getDestinationsCenterPosition(
  from?: { longitude: number; latitude: number },
  to?: { longitude: number; latitude: number },
): { longitude: number; latitude: number; zoom: number } {
  if (!from || !to) return { longitude: 0, latitude: 0, zoom: 1 }

  const fromLng = normalizeLongitude(from.longitude)
  const toLng = normalizeLongitude(to.longitude)

  const fromDestinationPoints = [fromLng, from.latitude]
  const toDestinationPoints = [toLng, to.latitude]

  const centerPosition = center(
    points([fromDestinationPoints, toDestinationPoints]),
  )
  const distanceValue = distance(fromDestinationPoints, toDestinationPoints)

  let zoom
  if (distanceValue > LONG_DISTANCE_ZOOM_LEVEL) {
    zoom = 6.5
  } else {
    zoom = Math.min(9, 10 - distanceValue / 100)
  }

  return {
    longitude: centerPosition.geometry.coordinates[0],
    latitude: centerPosition.geometry.coordinates[1],
    zoom,
  }
}

export function generateBoundsFromRoutes(routes: MapRoute[]): LngLatBoundsLike {
  if (!routes || routes.length === 0) {
    return [
      [0, 0],
      [0, 0],
    ]
  }

  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity

  routes.forEach((route) => {
    const normalizedLng = normalizeLongitude(route.longitude)

    minLng = Math.min(minLng, normalizedLng)
    minLat = Math.min(minLat, route.latitude)
    maxLng = Math.max(maxLng, normalizedLng)
    maxLat = Math.max(maxLat, route.latitude)
  })

  if (maxLng - minLng > 180) {
    const adjustedMinLng = minLng < 0 ? minLng : minLng - 360
    return [
      [adjustedMinLng, minLat],
      [maxLng, maxLat],
    ]
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}

export function calculatePaddingByVariables(
  firstPoint: MapRoute,
  secondPoint: MapRoute,
  isMobile: boolean,
): number {
  const distanceValue = distance(
    [firstPoint.longitude, firstPoint.latitude],
    [firstPoint.longitude, secondPoint.latitude],
  )

  const minZoomIn = isMobile ? 90 : 80
  const maxZoomOut = isMobile ? 115 : 110

  if (distanceValue > 20) {
    return Math.min(
      maxZoomOut,
      Math.round(minZoomIn + (distanceValue - 20) * 0.5),
    )
  }

  const paddingAdjustment = distanceValue * 0.25
  return Math.max(minZoomIn, Math.round(minZoomIn + paddingAdjustment))
}

export function calculateDynamicOffset(
  firstRoute: MapRoute,
  secondRoute: MapRoute,
): [number, number] {
  const deltaLongitude = Math.abs(secondRoute.longitude - firstRoute.longitude)
  const deltaLatitude = Math.abs(secondRoute.latitude - firstRoute.latitude)

  const totalDistance = Math.sqrt(deltaLongitude ** 2 + deltaLatitude ** 2)

  const verticalPercentage =
    totalDistance === 0 ? 0 : deltaLatitude / totalDistance

  const maxHorizontalOffset = -180
  const horizontalOffset = maxHorizontalOffset * verticalPercentage

  const verticalOffset = 0

  return [horizontalOffset, verticalOffset]
}

export const calculateMaxZoomBasedOnDistance = (
  firstRoute: MapRoute,
  secondRoute: MapRoute,
): number => {
  const distanceValue = distance(
    [firstRoute.longitude, firstRoute.latitude],
    [secondRoute.longitude, secondRoute.latitude],
  )

  if (distanceValue >= 60) return 9

  if (distanceValue >= 30) {
    const zoomFactor = (60 - distanceValue) / 20
    return 8.5 + zoomFactor * (11.2 - 8.5)
  }

  return 11.2
}
